/**
 * @fileoverview Live Support Context for screen sharing support sessions.
 *
 * Provides functionality for:
 * - Generating 6-digit support codes for users
 * - Connecting to support sessions as admin
 * - WebRTC screen sharing between user and admin
 * - WebSocket signaling for peer connection
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';

const LiveSupportContext = createContext();

/**
 * Custom hook to access the live support context.
 */
export const useLiveSupport = () => {
  const context = useContext(LiveSupportContext);
  if (!context) {
    throw new Error('useLiveSupport must be used within a LiveSupportProvider');
  }
  return context;
};

// WebRTC configuration
const RTC_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]
};

/**
 * Live Support Provider component.
 */
export const LiveSupportProvider = ({ children }) => {
  const { apiRequest, user } = useAuth();
  const { currentOrganization } = useOrganization();

  // Session state
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, generating, waiting, connecting, connected, sharing, error
  const [error, setError] = useState(null);
  const [sessionEndedByAdmin, setSessionEndedByAdmin] = useState(false);

  // For admin viewing
  const [remoteStream, setRemoteStream] = useState(null);

  // Refs for WebRTC and WebSocket
  const socketRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const hasCheckedActiveSession = useRef(false);

  // Get WebSocket URL based on environment
  const getSocketUrl = useCallback(() => {
    const isDev = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost');
    if (isDev) {
      return 'ws://localhost:5050';
    }
    // WebSocket must go through the API server (nginx proxies /socket.io/)
    return 'wss://api.clockwisecpa.app';
  }, []);

  // Clean up resources
  const cleanup = useCallback(() => {
    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Disconnect socket - disable reconnection first to prevent reconnect attempts
    if (socketRef.current) {
      socketRef.current.io.opts.reconnection = false;
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setRemoteStream(null);
  }, []);

  // Initialize WebSocket connection
  const initSocket = useCallback((sessionId, role) => {
    return new Promise((resolve, reject) => {
      const socketUrl = getSocketUrl();

      const socket = io(`${socketUrl}/support`, {
        withCredentials: true,
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      socket.on('connect', () => {
        console.log('[LiveSupport] Socket connected');
        socket.emit('join_session', { session_id: sessionId, role, org_id: currentOrganization });
      });

      socket.on('joined_session', (data) => {
        console.log('[LiveSupport] Joined session:', data);
        resolve(socket);
      });

      socket.on('connect_error', (err) => {
        console.error('[LiveSupport] Socket connection error:', err);
        reject(err);
      });

      socket.on('error', (data) => {
        console.error('[LiveSupport] Socket error:', data);
        setError(data.message);
      });

      // Handle admin joining (for user)
      socket.on('admin_joined', (data) => {
        console.log('[LiveSupport] Admin joined:', data);
        setStatus('connected');
      });

      // Handle user ready (for admin)
      socket.on('user_ready', (data) => {
        console.log('[LiveSupport] User ready:', data);
      });

      // Handle screen share started (for admin)
      socket.on('screen_share_started', () => {
        console.log('[LiveSupport] Screen share started');
        setStatus('sharing');
      });

      // Handle screen share stopped
      socket.on('screen_share_stopped', () => {
        console.log('[LiveSupport] Screen share stopped');
        setStatus('connected');
        setRemoteStream(null);
      });

      // Handle session ended
      socket.on('session_ended', (data) => {
        console.log('[LiveSupport] Session ended:', data);
        const reason = data?.reason;
        if (reason === 'admin_ended' || reason === 'completed') {
          setSessionEndedByAdmin(true);
        }
        cleanup();
        setSession(null);
        setStatus('idle');
      });

      // Handle disconnections
      socket.on('user_disconnected', () => {
        console.log('[LiveSupport] User disconnected');
        setStatus('waiting');
        setRemoteStream(null);
      });

      socket.on('admin_disconnected', () => {
        console.log('[LiveSupport] Admin disconnected');
        setStatus('waiting');
      });

      socketRef.current = socket;
    });
  }, [getSocketUrl, cleanup, currentOrganization]);

  // Set up WebRTC peer connection handlers
  const setupPeerConnection = useCallback((isSharer) => {
    const pc = new RTCPeerConnection(RTC_CONFIG);

    pc.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        socketRef.current.emit('ice_candidate', {
          session_id: session?.id,
          candidate: event.candidate,
          target: isSharer ? 'admin' : 'user'
        });
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('[LiveSupport] Connection state:', pc.connectionState);
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        setError('Connection lost');
      }
    };

    // For viewer (admin): handle incoming stream
    if (!isSharer) {
      pc.ontrack = (event) => {
        console.log('[LiveSupport] Received remote track:', event.track.kind);
        if (event.streams && event.streams[0]) {
          setRemoteStream(event.streams[0]);
        }
      };
    }

    peerConnectionRef.current = pc;
    return pc;
  }, [session?.id]);

  // ========================================
  // User Functions
  // ========================================

  /**
   * Generate a support code for the user.
   */
  const generateSupportCode = useCallback(async () => {
    setStatus('generating');
    setError(null);
    setSessionEndedByAdmin(false);

    try {
      const response = await apiRequest('/live-support/sessions', {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        setSession(data.session);
        setStatus('waiting');

        // Connect to WebSocket immediately so we can receive admin_joined events
        // Cookies are sent automatically with WebSocket connection
        try {
          console.log('[LiveSupport] Connecting to WebSocket for session:', data.session.id);
          await initSocket(data.session.id, 'user');
          console.log('[LiveSupport] WebSocket connected successfully');
        } catch (socketErr) {
          console.error('[LiveSupport] WebSocket connection failed:', socketErr);
          // Continue anyway - user can still share code, just won't get real-time updates
        }

        return data.session;
      } else {
        throw new Error(data.error || 'Failed to generate support code');
      }
    } catch (err) {
      setError(err.message);
      setStatus('error');
      throw err;
    }
  }, [apiRequest, initSocket]);

  /**
   * Start screen sharing (user).
   */
  const startScreenShare = useCallback(async () => {
    if (!session) {
      setError('No active session');
      return;
    }

    try {
      // Get screen stream
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          displaySurface: 'monitor'
        },
        audio: false
      });

      localStreamRef.current = stream;

      // Handle user stopping screen share via browser UI
      stream.getVideoTracks()[0].onended = () => {
        stopScreenShare();
      };

      // Initialize WebSocket if not connected
      if (!socketRef.current) {
        await initSocket(session.id, 'user');
      }

      // Close existing peer connection if any
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }

      // Remove old event listeners before adding new ones
      socketRef.current.off('rtc_answer');
      socketRef.current.off('ice_candidate');

      // Set up peer connection and add tracks
      const pc = setupPeerConnection(true);

      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });

      // Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      socketRef.current.emit('rtc_offer', {
        session_id: session.id,
        sdp: offer.sdp
      });

      // Handle answer
      socketRef.current.on('rtc_answer', async (data) => {
        if (pc.signalingState === 'have-local-offer') {
          await pc.setRemoteDescription(new RTCSessionDescription({
            type: 'answer',
            sdp: data.sdp
          }));
        }
      });

      // Handle ICE candidates
      socketRef.current.on('ice_candidate', async (data) => {
        if (data.candidate && pc.remoteDescription) {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        }
      });

      // Notify about screen share
      socketRef.current.emit('screen_share_started', { session_id: session.id });
      setStatus('sharing');

    } catch (err) {
      console.error('[LiveSupport] Error starting screen share:', err);
      setError(err.message);
    }
  }, [session, initSocket, setupPeerConnection]);

  /**
   * Stop screen sharing (user).
   */
  const stopScreenShare = useCallback(() => {
    // Stop local stream tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Remove WebRTC event listeners
    if (socketRef.current) {
      socketRef.current.off('rtc_answer');
      socketRef.current.off('ice_candidate');

      if (session) {
        socketRef.current.emit('screen_share_stopped', { session_id: session.id });
      }
    }

    setStatus('connected');
  }, [session]);

  /**
   * End the support session (user).
   */
  const endSession = useCallback(async () => {
    if (!session) return;

    try {
      await apiRequest(`/live-support/sessions/${session.id}`, {
        method: 'DELETE'
      });
    } catch (err) {
      console.error('[LiveSupport] Error ending session:', err);
    }

    if (socketRef.current) {
      socketRef.current.emit('session_ended', {
        session_id: session.id,
        reason: 'cancelled'
      });
    }

    cleanup();
    setSession(null);
    setStatus('idle');
  }, [session, apiRequest, cleanup]);

  // ========================================
  // Admin Functions
  // ========================================

  /**
   * Join a session by code (admin).
   */
  const joinSessionByCode = useCallback(async (code) => {
    setStatus('connecting');
    setError(null);

    try {
      const response = await apiRequest(`/live-support/sessions/code/${code}`);
      const data = await response.json();

      if (data.success) {
        setSession(data.session);

        // Connect to WebSocket (cookies sent automatically with withCredentials)
        await initSocket(data.session.id, 'admin');

        // Set up peer connection for receiving
        const pc = setupPeerConnection(false);

        // Handle incoming offer
        socketRef.current.on('rtc_offer', async (rtcData) => {
          console.log('[LiveSupport] Received offer');
          await pc.setRemoteDescription(new RTCSessionDescription({
            type: 'offer',
            sdp: rtcData.sdp
          }));

          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);

          socketRef.current.emit('rtc_answer', {
            session_id: data.session.id,
            sdp: answer.sdp
          });
        });

        // Handle ICE candidates
        socketRef.current.on('ice_candidate', async (iceData) => {
          if (iceData.candidate) {
            try {
              await pc.addIceCandidate(new RTCIceCandidate(iceData.candidate));
            } catch (err) {
              console.warn('[LiveSupport] Error adding ICE candidate:', err);
            }
          }
        });

        setStatus('connected');
        return data.session;
      } else {
        throw new Error(data.error || 'Invalid support code');
      }
    } catch (err) {
      setError(err.message || 'Failed to join session');
      setStatus('error');
      throw err;
    }
  }, [apiRequest, initSocket, setupPeerConnection]);

  /**
   * Leave the session (admin).
   */
  const leaveSession = useCallback(() => {
    if (socketRef.current && session) {
      socketRef.current.emit('session_ended', {
        session_id: session.id,
        reason: 'completed'
      });
    }

    cleanup();
    setSession(null);
    setStatus('idle');
  }, [session, cleanup]);

  // ========================================
  // Check for active session on mount and reconnect
  // ========================================

  useEffect(() => {
    const checkActiveSession = async () => {
      // Only check once per mount and only when user is available
      if (!user) {
        console.log('[LiveSupport] Waiting for user to be available...');
        return;
      }

      if (hasCheckedActiveSession.current) {
        return;
      }

      hasCheckedActiveSession.current = true;

      try {
        const response = await apiRequest('/live-support/sessions/active');
        const data = await response.json();

        if (data.success && data.session) {
          console.log('[LiveSupport] Found active session on mount:', data.session.id, 'status:', data.session.status);
          setSession(data.session);

          // Determine status based on session state from backend
          // Backend status values: "pending", "connected" (admin_connected), "sharing"
          const sessionStatus = data.session.status;
          const hasAdmin = !!data.session.admin_id;

          if (sessionStatus === 'sharing') {
            // Screen share was active but can't be restored - show as connected
            setStatus('connected');
          } else if (sessionStatus === 'connected' || hasAdmin) {
            // Admin has connected
            setStatus('connected');
          } else {
            // pending, no admin yet
            setStatus('waiting');
          }

          console.log('[LiveSupport] Set status based on session:', { sessionStatus, hasAdmin });

          // Reconnect to WebSocket to receive events
          try {
            console.log('[LiveSupport] Reconnecting to WebSocket for session:', data.session.id);
            await initSocket(data.session.id, 'user');
            console.log('[LiveSupport] WebSocket reconnected successfully');
          } catch (socketErr) {
            console.error('[LiveSupport] WebSocket reconnection failed:', socketErr);
          }
        } else {
        }
      } catch (err) {
        // No active session or error, that's fine
        console.log('[LiveSupport] Error checking active session:', err.message);
      }
    };

    checkActiveSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const clearSessionEndedByAdmin = useCallback(() => {
    setSessionEndedByAdmin(false);
  }, []);

  const value = {
    // State
    session,
    status,
    error,
    remoteStream,
    sessionEndedByAdmin,

    // User functions
    generateSupportCode,
    startScreenShare,
    stopScreenShare,
    endSession,

    // Admin functions
    joinSessionByCode,
    leaveSession,

    // Utilities
    isConnected: status === 'connected' || status === 'sharing',
    isSharing: status === 'sharing',
    hasActiveSession: !!session,
    clearSessionEndedByAdmin,
  };

  return (
    <LiveSupportContext.Provider value={value}>
      {children}
    </LiveSupportContext.Provider>
  );
};

export default LiveSupportContext;
